
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState } from "react";
import { DataTablePagination } from "@/components/ui/data-table-pagination";

interface AttendanceListProps {
  eventId: string;
  participants: any[];
  isLoading: boolean;
}

export function AttendanceList({ participants, isLoading }: AttendanceListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredParticipants = participants.filter(
    (participant) =>
      participant.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      participant.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      participant.level.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedParticipants = filteredParticipants.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search participants..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Registration Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedParticipants.length > 0 ? (
              paginatedParticipants.map((participant) => (
                <TableRow key={participant.registration_id}>
                  <TableCell className="font-medium">
                    {participant.full_name}
                  </TableCell>
                  <TableCell>{participant.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{participant.level}</Badge>
                  </TableCell>
                  <TableCell>
                    {format(
                      new Date(participant.registration_date),
                      "MMM d, yyyy HH:mm"
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center h-24">
                  {searchQuery
                    ? "No participants found matching your search"
                    : "No participants registered for this event"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={filteredParticipants.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
